import type { ReactNode } from "react";
import { Bot, CheckCircle2, ExternalLink, FlaskConical, XCircle } from "lucide-react";
import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Collapsible, CollapsibleContent, CollapsibleTrigger, EmptyState, MarkdownBody } from "@ncp/ui";
import { text, type PageDetail } from "../api";

interface EvalRun { agent: string; definitionId?: string; version?: number; succeeded: boolean; passed: number; total: number; redTeamPassed: number; redTeamTotal: number; error?: string | null; cases?: Array<{ name: string; passed: boolean; detail?: string | null }> }

/** The page's agents as page.json and agents/*.agent.json declare them, with their evals and the result of the last evaluation. */
export function AgentsTab({ detail, onOpenFile, onOpenAgent }: { detail: PageDetail; onOpenFile: (path: string) => void; onOpenAgent: (key: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const agents = detail.agents ?? [];
  const check = detail.validation?.checks.find((c) => c.key === "agents");
  const runs = ((check?.detail as { agents?: EvalRun[] } | undefined)?.agents ?? []);
  const tools = detail.clientTools ?? [];

  if (agents.length === 0) return <EmptyState icon={Bot} title={t("admin:pageStudio.agents.emptyTitle")} description={t("admin:pageStudio.agents.emptyBody")} />;

  return (
    <div className="space-y-4">
      {agents.map((a) => {
        const run = runs.find((r) => r.agent === a.key);
        const file = `agents/${a.key}.agent.json`;
        const hasFile = detail.files.some((f) => f.path === file);
        return (
          <Card key={a.key}>
            <CardHeader className="flex-row items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="flex flex-wrap items-center gap-2"><Bot className="size-4 text-aegold-700" /> {text(a.name, locale) || a.key}
                  <code className="font-mono text-xs font-normal text-muted-foreground" dir="ltr">{a.key}</code>
                  {a.tier ? <Badge variant="outline" size="sm">{a.tier}</Badge> : null}
                  {a.model ? <Badge variant="outline" size="sm">{a.model}</Badge> : null}
                </CardTitle>
                <CardDescription>{text(a.greeting, locale)}</CardDescription>
              </div>
              <div className="flex shrink-0 gap-1">
                {hasFile ? <Button size="sm" variant="ghost" onClick={() => onOpenFile(file)}>{t("admin:pageStudio.agents.openFile")}</Button> : null}
                {detail.page.status === "Published" ? <Button size="sm" variant="ghost" onClick={() => onOpenAgent(`page.${detail.page.key}.${a.key}`)}><ExternalLink /> {t("admin:pageStudio.agents.definition")}</Button> : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <dl className="grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4">
                <Item label={t("admin:pageStudio.agents.tools")} value={a.tools?.length ? <span className="flex flex-wrap gap-1">{a.tools.map((x) => <Badge key={x} variant="outline" size="sm"><code dir="ltr">{x}</code></Badge>)}</span> : "—"} />
                <Item label={t("admin:pageStudio.agents.knowledge")} value={a.knowledge?.length ? a.knowledge.join(", ") : "—"} />
                <Item label={t("admin:pageStudio.agents.guardrails")} value={a.guardrails ?? t("admin:pageStudio.agents.standard")} />
                <Item label={t("admin:pageStudio.agents.budget")} value={[a.tokensPerRun ? t("admin:pageStudio.agents.tokensPerRun", { count: formatNumber(a.tokensPerRun, locale) }) : null, a.monthlyBudgetUsd ? `$${a.monthlyBudgetUsd}/${t("admin:pageStudio.agents.month")}` : null].filter(Boolean).join(" · ") || "—"} />
              </dl>
              {a.instructions ? (
                <Collapsible>
                  <CollapsibleTrigger className="text-xs font-medium text-aegold-700 underline-offset-2 hover:underline">{t("admin:pageStudio.agents.instructions")}</CollapsibleTrigger>
                  <CollapsibleContent><div className="mt-2 max-h-80 overflow-auto rounded-lg bg-sand-50 p-3 text-sm"><MarkdownBody>{a.instructions}</MarkdownBody></div></CollapsibleContent>
                </Collapsible>
              ) : null}
              <section>
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <FlaskConical className="size-3.5 text-aegold-700" />
                  <span className="text-xs font-semibold">{t("admin:pageStudio.agents.evals", { count: a.evals?.length ?? 0 })}</span>
                  {run ? (
                    <Badge size="sm" variant={run.error ? "danger" : run.succeeded ? "success" : "danger"}>
                      {run.error ? t("admin:pageStudio.agents.evalError") : t("admin:pageStudio.agents.evalResult", { passed: run.passed, total: run.total, red: run.redTeamPassed, redTotal: run.redTeamTotal })}
                    </Badge>
                  ) : <span className="text-[11px] text-muted-foreground">{t("admin:pageStudio.agents.notEvaluated")}</span>}
                </div>
                {run?.error ? <p className="mb-1 text-xs text-danger-700">{run.error}</p> : null}
                <ul className="space-y-1">
                  {(a.evals ?? []).map((e) => {
                    const c = run?.cases?.find((x) => x.name === e.name);
                    return (
                      <li key={e.name} className="flex items-start gap-2 rounded-md border border-sand-100 px-2 py-1.5 text-xs">
                        {c ? (c.passed ? <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success-600" /> : <XCircle className="mt-0.5 size-3.5 shrink-0 text-danger-600" />) : <span className="mt-1 size-2 shrink-0 rounded-full bg-sand-300" />}
                        <span className="min-w-0 flex-1"><span className="font-medium">{e.name}</span><span className="block text-muted-foreground">{e.brief}</span>{c && !c.passed && c.detail ? <span className="block text-danger-700">{c.detail}</span> : null}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </CardContent>
          </Card>
        );
      })}
      {tools.length > 0 ? (
        <Card>
          <CardHeader><CardTitle>{t("admin:pageStudio.agents.clientTools")}</CardTitle><CardDescription>{t("admin:pageStudio.agents.clientToolsHint")}</CardDescription></CardHeader>
          <CardContent><ul className="space-y-1 text-sm">{tools.map((c) => <li key={c.name}><code className="font-mono text-xs" dir="ltr">client.{c.name}</code> <span className="text-muted-foreground">{c.description}</span></li>)}</ul></CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Item({ label, value }: { label: string; value: ReactNode }) {
  return <div className="rounded-lg bg-sand-50 px-2 py-1.5"><dt className="text-[10px] text-muted-foreground">{label}</dt><dd className="mt-0.5">{value}</dd></div>;
}
